import * as React from 'react';
import TextField from 'material-ui/TextField';
import ReactTable from 'react-table';
import Phone from 'react-phone-number-input';
import CommonFeatures from '../commons/CommonFeatures';
import RaisedButton from 'material-ui/RaisedButton';
import { EmailBlastFilters, AdminState, ImportCsvResult, Prospect } from './admin.types';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import Dialog from 'material-ui/Dialog';
import FileUploadComponent from '../util/fileUploadComponent';
import { BusinessState } from 'src/business/business.types';
import { AuthState } from 'src/auth/auth.types';

type Props = {
  readonly adminState?: AdminState
  readonly authState: AuthState
  readonly businessState: BusinessState
  readonly emailBlast?: (subject: string, emailBody: string, filters?: EmailBlastFilters) => Promise<Response>
  readonly importCSVAction?: (file: File) => Promise<Response>
};

type TableData = {
  readonly name: string
  readonly email: string
  readonly phone: JSX.Element
  readonly type: string
};

type States = {
  readonly emailSubject: string
  readonly success: boolean
  readonly failure: boolean
  readonly loading: boolean
  readonly openAddFiltersModal: boolean
  readonly openImportCSVModal: boolean
  readonly city: string
  readonly province: string
  readonly country: string
  readonly businessType: string
  readonly csvFile?: File
  readonly importCsvLoading: boolean
  readonly importCsvResult: ImportCsvResult
  readonly emailBody: string
};

class EmailBlastComponent extends React.Component<Props, States> {
  
  constructor(props: Props) {
    super(props);

    this.state = {
      emailSubject: undefined,
      success: false,
      failure: false,
      loading: false,
      openAddFiltersModal: false,
      openImportCSVModal: false,
      city: undefined,
      province: undefined,
      country: undefined,
      businessType: undefined,
      csvFile: undefined,
      importCsvLoading: false,
      importCsvResult: undefined,
      emailBody: undefined
    };
  }

  componentWillUnmount() {
    this.setState({
      emailSubject: undefined,
      emailBody: undefined,
      success: false,
      failure: false,
      loading: false,
      openAddFiltersModal: false,
      openImportCSVModal: false,
      city: undefined,
      province: undefined,
      country: undefined,
      businessType: undefined
    });
  }

  render() {
    return (
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2">
          <div className="col-auto">
            <h3>Email Blast</h3>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12">
            <TextField
              hintText="Subject"
              floatingLabelText="Subject"
              onChange={this.onEmailSubjectChange}
              multiLine={true}
              rows={1}
            />
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12">
            <TextField
              hintText="Email Body"
              floatingLabelText="Email Body"
              onChange={this.onEmailBodyChange}
              fullWidth={true}
              multiLine={true}
              rows={10}
            />
          </div>
        </div>
        <div className="row mt-2 justify-content-end">
          <div className="col-auto red">
            {this.state.failure ? 'Something went wrong. Please check inputs and filters' : ''}
          </div>
          <div className="col-auto green">
            {this.state.success ? `Sent ${this.props.adminState.sentEmails} Emails Sucessfully` : ''}
          </div>
          <div className="col-auto">
            {
              this.state.loading ?
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                /> :
                this.renderSendEmailsButton()
            }
          </div>
          <div className="col-auto">
            {this.renderAddFiltersButton()}
          </div>
          <div className="col-auto">
            {this.renderImportCSVButton()}
          </div>
        </div>
        {this.renderAddFiltersModal()}
        {this.renderImportCSVModal()}
      </CommonFeatures>
    );
  }

  onEmailSubjectChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({emailSubject: newValue})

  onEmailBodyChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({emailBody: newValue})

  renderSendEmailsButton = (): JSX.Element => {
    return(
      <RaisedButton
        primary={true}
        label="Send Emails"
        onClick={() => this.props.emailBlast ?
          this.setState(
            {loading: true},
            () => {
              this.props.emailBlast(
                this.state.emailSubject === undefined || this.state.emailSubject === '' ? 
                  undefined : 
                  this.state.emailSubject,
                this.state.emailBody === undefined || this.state.emailBody === '' ?
                  undefined :
                  this.state.emailBody,
                {
                  city: this.state.city,
                  province: this.state.province,
                  country: this.state.country,
                  businessType: this.state.businessType
                }
              )
              .then(res => {
                switch (this.props.adminState.statusCode) {
                  case 200:
                    this.setState({success: true, failure: false, loading: false});
                    break;
                  case 400:
                    this.setState({failure: true, success: false, loading: false});
                    break;
                  default:
                    this.setState({failure: false, success: false, loading: false});
                    break;
                }
              });
            }
          ) :
          undefined
        }
      />
    );
  }

  renderAddFiltersButton = (): JSX.Element => {
    return(
      <RaisedButton
        primary={true}
        label="Add Filters"
        onClick={() => this.setState({openAddFiltersModal: true})}
      />
    );
  }

  renderImportCSVButton = (): JSX.Element => {
    return(
      <RaisedButton
        secondary={true}
        label="Import CSV"
        onClick={() => this.setState({openImportCSVModal: true})}
      />
    );
  }

  renderAddFiltersModal = (): JSX.Element => {
    return(
      <Dialog
        title="Add Filters"
        actions={[this.renderAddButton(), this.renderCancelButton()]}
        modal={true}
        open={this.state.openAddFiltersModal}
      >
        <div className="row">
          <div className="col-auto">
            <TextField
              hintText="City"
              floatingLabelText="City"
              value={this.state.city}
              onChange={this.onCityChange}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-auto">
            <TextField
              hintText="Province"
              floatingLabelText="Province"
              value={this.state.province}
              onChange={this.onProvinceChange}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-auto">
            <TextField
              hintText="Country"
              floatingLabelText="Country"
              value={this.state.country}
              onChange={this.onCountryChange}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-auto">
            <TextField
              hintText="Business Type"
              floatingLabelText="Business Type"
              value={this.state.businessType}
              onChange={this.onBusinessTypeChange}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  renderImportCSVModal = (): JSX.Element => {
    return(
      <Dialog
        title="Import CSV"
        actions={[this.renderCancelButton()]}
        modal={true}
        open={this.state.openImportCSVModal}
      >
        <div className="row">
          <div className="col-auto">
            <FileUploadComponent
              onFileChangeHandler={this.onCsvFileChangeHandler}
              fileExtensions={['csv']}
            />
          </div>
          <div className="col-auto">
            {
              this.state.importCsvLoading ?
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                /> :
                <RaisedButton
                  primary={true}
                  disabled={this.state.csvFile === undefined}
                  label="Upload"
                  onClick={this.importCSV}
                />
              }
          </div>
        </div>
        {
          (this.state.importCsvResult !== undefined) ?
          <div className="row">
            <div className="col-12 pt-4">
              <div>
                Successfuly Registered: <strong>{this.state.importCsvResult.registered}</strong>
              </div>
              <ReactTable
                data={this.state.importCsvResult.failedProspects
                  .map((prospect: Prospect) => this.createTableData(prospect))}
                columns={[
                  {
                    accessor: 'name',
                    Header: 'Name',
                    style: {textAlign: 'center'}
                  },
                  {
                    accessor: 'email',
                    Header: 'Email',
                    style: {textAlign: 'center'}
                  },
                  {
                    accessor: 'phone',
                    Header: 'Phone',
                    style: {textAlign: 'center'}
                  },
                  {
                    accessor: 'type',
                    Header: 'Type',
                    style: {textAlign: 'center'}
                  }
                ]}
                defaultPageSize={5}
                className="-striped -highlight"
              />
            </div>
          </div> :
          null
        }
      </Dialog>
    );
  }

  createTableData = (prospect: Prospect): TableData => {
    return {
      name: prospect.businessName,
      email: prospect.email,
      phone: (
        <Phone
          countries={['CA']}
          value={prospect.phone}
          disabled={true}
          convertToNational={true}
          inputStyle={{
            backgroundColor: 'transparent',
            borderBottom: '0px',
            height: 19
          }}
        />
      ),
      type: prospect.businessType
    };
  }

  onCsvFileChangeHandler = (csvfile: File): void => {
    this.setState({
      csvFile: csvfile
    });
  }

  importCSV = (): void => {
    const { importCSVAction } = this.props;
    const { csvFile } = this.state;

    if (csvFile !== undefined) {
      this.setState(
        {importCsvLoading: true},
        () => {
          importCSVAction(csvFile)
          .then(res => {
            switch (this.props.adminState.statusCode) {
              case 200:
                this.setState({
                  importCsvResult: this.props.adminState.importCsvResult,
                  importCsvLoading: false
                });
                break;
              case 400:
                this.setState({importCsvLoading: false});
                break;
              default:
                this.setState({importCsvLoading: false});
                break;
            }
          });
        }
      );
    }
  }

  onCityChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({city: newValue === '' ? undefined : newValue})

  onProvinceChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({province: newValue === '' ? undefined : newValue})
  
  onCountryChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({country: newValue === '' ? undefined : newValue})

  onBusinessTypeChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({businessType: newValue === '' ? undefined : newValue})

  renderAddButton = (): JSX.Element => {
    return(
      <RaisedButton
        primary={true}
        label="Add"
        onClick={() => this.setState({
          openAddFiltersModal: false
        })}
      />
    );
  }

  renderCancelButton = (): JSX.Element => {
    const { openAddFiltersModal } = this.state;

    return(
      (openAddFiltersModal) ? (
        <RaisedButton
          primary={true}
          label="Cancel"
          onClick={() => this.setState({
            openAddFiltersModal: false,
            city: undefined,
            province: undefined,
            country: undefined,
            businessType: undefined
          })}
        />
      ) : (
        <RaisedButton
          primary={true}
          label="Close"
          onClick={() => this.setState({
            openImportCSVModal: false,
          })}
        />
      )
    );
  }
}

export default EmailBlastComponent;



// WEBPACK FOOTER //
// ./src/admin/EmailBlastComponent.tsx